import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import StatusPage from './App.js';
import { PLANNER_PATH, STATUS_PATH, canonicalPath } from './routes.js';

/**
 * The planner, and with it MapLibre, loaded only when someone asks for it.
 *
 * MapLibre is by far the largest dependency in this application. Splitting it
 * behind the route means the service page never downloads a mapping engine it
 * has no use for.
 */
const JourneyPlannerRoute = lazy(async () => import('./planner/JourneyPlannerRoute.js'));

/**
 * A two-page router, written out rather than installed.
 *
 * React Router would be a reasonable choice at four pages; at two it is a
 * dependency, a lifecycle and a set of conventions in exchange for `pushState`
 * and a `popstate` listener. The moment a third route with parameters appears,
 * replacing this with a real router is a contained change.
 *
 * There is no navigation bar. The planner is a full-height instrument beside a
 * map, and a strip of tabs above it would cost a line of the map on every
 * screen in the product to offer a link each page can carry itself. Each page
 * links to the other, once, where it belongs.
 */
export default function Router(): React.JSX.Element {
  const [path, setPath] = useState(() =>
    typeof window === 'undefined' ? PLANNER_PATH : canonicalPath(window.location.pathname),
  );

  useEffect(() => {
    const onPopState = (): void => setPath(canonicalPath(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  /*
   * `/plan` is rewritten to `/` in place.
   *
   * `replaceState`, not `pushState`: the old address is not a page somebody
   * navigated to and pressing Back must not return to it, which would bounce
   * them straight here again. The search and the hash are carried across
   * untouched, so every parameter of a shared journey survives the rewrite and
   * the planner reads exactly what it would have read at `/plan`.
   */
  useEffect(() => {
    if (window.location.pathname === PLANNER_PATH) return;
    if (canonicalPath(window.location.pathname) !== PLANNER_PATH) return;
    window.history.replaceState(
      window.history.state,
      '',
      `${PLANNER_PATH}${window.location.search}${window.location.hash}`,
    );
  }, [path]);

  const navigate = useCallback((next: string) => {
    window.history.pushState({}, '', next);
    setPath(canonicalPath(next));
  }, []);

  if (path === STATUS_PATH) {
    return <StatusPage onNavigate={navigate} />;
  }

  return (
    <Suspense fallback={<p className="route-loading">A carregar o planeador…</p>}>
      <JourneyPlannerRoute onNavigate={navigate} />
    </Suspense>
  );
}
